import * as React from 'react';
import DashboardEntry from './dashboard-entry';
import { AppContext } from '../../shared/app-context';
import useDashboard from '../../hooks/useDashboard';
import { CircularProgress } from '@material-ui/core';
import * as styles from './dashboard.module.scss';
import { RouteComponentProps } from 'react-router';
import useTimedAlerts from '../../hooks/useTimedAlerts';
import { SecondaryAlert } from 'kagr-ui';

interface DashboardLocationState {
  error?: string;
  redirect?: boolean;
}

/**
 * @function Dashboard Outer component for the dashboard page
 */
const Dashboard = ({ location }: RouteComponentProps<{}, any, DashboardLocationState>) => {
  const appContext = React.useContext(AppContext);
  const { clientsQuery, dashboardsQuery } = useDashboard();
  const { errorMessage, setErrorMessage } = useTimedAlerts();

  React.useEffect(() => {
    if (!appContext.state.clientChanging) {
      // if redirected from a non-existent report, show accompanying error in location state
      if (location.state && location.state.error) {
        setErrorMessage(location.state.error);
      }
    }

    appContext.setState({
      clientChanging: false
    });
  }, []);

  /**
   * @function renderData - Renders the data to be displayed on the main dashboard
   */
  const renderData = () => {
    if (dashboardsQuery.data && dashboardsQuery.data.dashboards.length === 0) {
      return <SecondaryAlert type="warning" message="No dashboards available to display" />;
    }

    if (dashboardsQuery.data) {
      return dashboardsQuery.data.dashboards.map((dashboard) => (
        <DashboardEntry key={dashboard.id} dashboard={dashboard} />
      ));
    }

    if (dashboardsQuery.isError) {
      return <SecondaryAlert type="error" message={dashboardsQuery.error.message} />;
    }
  };

  return (
    <div className={styles.container}>
      {errorMessage && <SecondaryAlert type="error" message={errorMessage} />}
      <h1>Dashboards</h1>
      {clientsQuery.isLoading || dashboardsQuery.isLoading ? (
        <div className="loadingIndicator">
          <CircularProgress />
        </div>
      ) : (
        renderData()
      )}
    </div>
  );
};

export default Dashboard;
